import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Login from './Login';
import AdminDashboard from './AdminDashboard';
import PublicFormPage from './pages/PublicFormPage';
import { isUserAuthenticated } from './api/auth';
import { sessionManager } from './api/sessionManager';
import './api/debugUtils'; // Import debug utilities

// Protected Route Component
const ProtectedRoute = ({ children, isAuthenticated }) => {
  if (!isAuthenticated) {
    console.log('🚫 User not authenticated, redirecting to login');
    return <Navigate to="/" replace />;
  }
  console.log('✅ User authenticated, showing protected content');
  return children;
};

// Main App Content (inside Router)
const AppContent = ({ isAuthenticated, handleLoginSuccess, handleLogout }) => {
  return (
    <Routes>
      {/* Login Route */}
      <Route 
        path="/" 
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login onLoginSuccess={handleLoginSuccess} />
          )
        } 
      />

      {/* Public Apply Route */}
      <Route path="/apply" element={<PublicFormPage />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <AdminDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* Redirect unknown routes based on auth state */}
      <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />} />
    </Routes>
  );
};

function App() {
  console.log('🚀 App initializing...');
  
  // Check if user is already authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    console.log('📱 Checking initial authentication state...');
    const isAuth = isUserAuthenticated();
    console.log('🔐 Initial auth state:', isAuth);
    return isAuth;
  });

  // Restore session when app loads and listen for storage changes
  useEffect(() => {
    console.log('🔄 App mounted, checking for existing session...');
    
    const isAuth = isUserAuthenticated();
    console.log('🔍 Session check result:', isAuth);
    
    setIsAuthenticated(isAuth);
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e) => {
      console.log('📡 Storage changed in another tab:', e.key);
      if (e.key === 'banking_crm_token') {
        const newAuthState = !!e.newValue;
        console.log('🔄 Updating auth state from storage event:', newAuthState);
        setIsAuthenticated(newAuthState);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLoginSuccess = useCallback(() => {
    console.log('✅ Login successful, verifying stored token...');
    
    // Verify token is in storage before updating state
    const token = sessionManager.getToken();
    if (token) {
      console.log('🔑 Token verified in storage, updating auth state');
      setIsAuthenticated(true);
    } else {
      console.error('❌ Token not found in storage after login');
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    console.log('🔓 Logout called, clearing session');
    setIsAuthenticated(false);
  }, []);

  return (
    <Router>
      <AppContent 
        isAuthenticated={isAuthenticated} 
        handleLoginSuccess={handleLoginSuccess}
        handleLogout={handleLogout}
      />
    </Router>
  );
}

export default App;
